/* eslint eqeqeq: 'off' */
/* eslint no-param-reassign: 'off' */
/*

The MIT License (MIT)

Copyright (c) 2015 Chris Corbyn.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/
'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.Addrv4 = void 0;
exports.getOverlappingCidrBlocks = getOverlappingCidrBlocks;
/*! The address used when not specified */
const DEFAULT_ADDRESS = 0;
/*! The prefix length used when not specified */
const DEFAULT_PREFIX = 32;
/*! The lowest permissible address */
const MIN_ADDRESS = 0;
/*! The highest permissible address */
const MAX_ADDRESS = 0xffffffff;
function getOverlappingCidrBlocks(cidrBlocks) {
    for (let i = 0; i < cidrBlocks.length; ++i) {
        for (let j = 0; j < cidrBlocks.length; ++j) {
            if (i === j) {
                continue;
            }
            const cidr1 = cidrBlocks[i];
            const cidr2 = cidrBlocks[j];
            const addr1 = Addrv4.make(cidr1);
            const addr2 = Addrv4.make(cidr2);
            const isOverlappingCidrBlocks = addr1.intersect(addr2) !== undefined;
            if (isOverlappingCidrBlocks) {
                return { cidr1: addr1, cidr2: addr2 };
            }
        }
    }
    return undefined;
}
class Addrv4 {
    /**
     * Representation of an IPv4 address or CIDR.
     *
     * For convenience, this constructor may be invoked as a regular function.
     *
     * @constructor
     *
     * @param {Object} address
     *   either a string or an integer
     *
     * @param {Number} prefix, optional
     *   the length of the netmask prefix
     *
     * @throws {Error}
     *   if the address is not valid
     */
    static make(address, prefix) {
        try {
            switch (typeof address) {
                case 'undefined':
                    return Addrv4.make(DEFAULT_ADDRESS, prefix);
                case 'string':
                    return Addrv4.parseString(address, prefix);
                case 'number':
                    return Addrv4.parseNumber(address, prefix);
                default:
                    throw new Error(`Invalid Addr type: ${typeof address}`);
            }
        }
        catch (e) {
            throw new Error(`Invalid Addr: ${address}: ${e.toString()}`);
        }
    }
    /*! Parse an IPv4 address string with optional prefix */
    static parseString(address, prefix) {
        const parts = address.split('/');
        if (parts.length > 2) {
            throw new Error(`Invalid Addr: ${address}`);
        }
        const octets = parts[0].split('.').map((n) => Addrv4.parseByte(n, 255));
        const intval = Addrv4.octetsToInt(octets);
        const prefixByte = Addrv4.parseByte(typeof prefix === 'undefined' ? parts[1] || DEFAULT_PREFIX.toString() : prefix.toString(), 32);
        return new Addrv4(intval, octets, prefixByte);
    }
    /*! Parse an IPv4 address integer with optional prefix */
    static parseNumber(address, prefix) {
        if (address > MAX_ADDRESS || address < MIN_ADDRESS) {
            throw new Error(`Invalid Addrv4 ${address}`);
        }
        const octets = [255, 255, 255, 255]
            .map(function (octet, offset) {
            return (address >>> (offset * 8)) & octet;
        })
            .reverse();
        const intval = Addrv4.octetsToInt(octets);
        const prefixByte = Addrv4.parseByte(typeof prefix === 'undefined' ? DEFAULT_PREFIX.toString() : prefix.toString(), 32);
        return new Addrv4(intval, octets, prefixByte);
    }
    constructor(intval, octets, prefix) {
        this.intval = intval;
        this.octets = octets;
        this.prefix = prefix;
        void this.octets;
    }
    /*! Convert the given octets to an integer */
    static octetsToInt(octets) {
        return [...octets]
            .reverse()
            .map(function (octet, offset) {
            return octet * (1 << (offset * 8));
        })
            .reduce(function (x, y) {
            return x + y;
        });
    }
    /**
     * Perform a parseInt(), only if a valid byte value with an optional max.
     *
     * @param {String} n
     *   the value to parse
     *
     * @param {Number} max
     *   the maximum allowed value
     *
     * @return {Number}
     *   the parsed integer
     */
    static parseByte(n, max) {
        max = max || 255;
        if (/^[0-9]+$/.test(n)) {
            const m = parseInt(n);
            if (m <= max) {
                return m;
            }
        }
        throw new Error(`${n}: must be a decimal value <= ${max}`);
    }
    /**
     * Return the CIDR range that is the intersection of `this` and `other`.
     *
     * Because CIDR subnets cannot possibly partially overlap, in practice this
     * method returns either `this` or `other` where there is a match. The
     * returned Addrv4 will always be the smallest range.
     *
     * @param {Addrv4} other
     *   another Addrv4 to intersect with
     *
     * @return {Addrv4}
     *   the intersection of the two Addrv4, or undefined if not possible
     */
    intersect(other) {
        return (this.contains(other) && other) || (other.contains(this) && this) || undefined;
    }
    /**
     * Return true if this Addrv4 completely contains another Addrv4.
     *
     * @param {Addrv4} other
     *   the other Addrv4 to check
     *
     * @return {Boolean}
     *   true if other is a subset of this, false if not
     */
    contains(other) {
        return this.onNetwork(other.network()) && this.onNetwork(other.broadcast());
    }
    /*! Return true if a fixed IP is on this network */
    onNetwork(ip) {
        return (this.netmask().toInt() & ip.toInt()) >>> 0 == this.network().toInt();
    }
    /**
     * Return the broadcast address from this Addr.
     *
     * This equates to the last address in the CIDR range.
     *
     * @return {Number}
     *   the broadcast address
     */
    broadcast() {
        return Addrv4.make((this.network().toInt() | ~this.netmask().toInt()) >>> 0);
    }
    /**
     * Return the network address from this Addrv4.
     *
     * This equates to the first address in the CIDR range.
     *
     * @return {Addrv4}
     *   the network address
     */
    network() {
        return Addrv4.make((this.toInt() & this.netmask().toInt()) >>> 0);
    }
    /**
     * Return the integer representation of this Addrv4.
     *
     * @return {Number}
     *   the integer value of the Addrv4
     */
    toInt() {
        return this.intval;
    }
    /**
     * Return the netmask value for this Addrv4.
     *
     * @return {Addrv4}
     *   the netmask
     */
    netmask() {
        switch (this.prefix) {
            case 0:
                return Addrv4.make(0x00000000);
            case 32:
                return Addrv4.make(0xffffffff);
            default:
                return Addrv4.make(~(0xffffffff >>> this.prefix) >>> 0);
        }
    }
    /**
     * Return the string representation of this Addr.
     *
     * @return {String}
     *   the string representation, including the mask
     */
    toString() {
        return [this.octets.join('.'), this.prefix].join('/');
    }
}
exports.Addrv4 = Addrv4;
//# sourceMappingURL=data:application/json;base64,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